import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HardHat } from 'lucide-react'
import type { SiteDocListRow } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { fmtDate } from '@/lib/format-date'
import { formCard } from '@/lib/form-styles'

/**
 * AP-46 10.4 — **Karte „Baustellendokumente" im Projekt** (nur mit Projektverwaltung): die Dokumente des
 * Projekts mit Zustand, ein Klick öffnet sie in der Akte. Liegt unter `components/`, weil das Projekt
 * nicht in ein anderes Feature greifen darf (AP-21-B); gelesen wird über den Endpunkt der Akte.
 */
export function ProjektBaustellendokumente({ projectId }: { projectId: string }) {
  const { t } = useTranslation()
  const [zeilen, setZeilen] = useState<SiteDocListRow[] | null>(null)
  const [fehler, setFehler] = useState(false)

  useEffect(() => {
    let aktiv = true
    api
      .get<{ data: SiteDocListRow[]; onDevice: SiteDocListRow[] }>('/site-docs', { params: { projectId, limit: 50, sort: 'documentDate', dir: 'desc' } })
      .then((r) => aktiv && setZeilen([...r.data.onDevice, ...r.data.data]))
      .catch(() => aktiv && setFehler(true))
    return () => {
      aktiv = false
    }
  }, [projectId])

  return (
    <div className={formCard}>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-medium">
        <HardHat className="h-4 w-4" aria-hidden /> {t('siteDocs.title')}
      </h2>
      {fehler && <p className="text-sm text-destructive">{t('siteDocs.list.loadError')}</p>}
      {zeilen && zeilen.length === 0 && <p className="text-sm text-muted-foreground">{t('siteDocs.dashboard.empty')}</p>}
      <ul className="divide-y divide-border">
        {zeilen?.map((z) => (
          <li key={z.onDevice ? `g-${z.id}` : z.id} className={`flex items-center gap-3 py-1.5 text-sm ${z.onDevice ? 'italic text-muted-foreground' : ''}`}>
            <span className="w-20 shrink-0 text-xs text-muted-foreground">{fmtDate(z.documentDate)}</span>
            {z.onDevice ? (
              <span className="min-w-0 flex-1 truncate">
                {t(`siteDocs.kinds.${z.kind}`)} {z.number ?? ''}
              </span>
            ) : (
              <Link to={`/site-docs/${z.id}`} className="min-w-0 flex-1 truncate hover:underline">
                <span className="mr-2 font-mono text-xs text-muted-foreground">{z.number ?? t(`siteDocs.kinds.${z.kind}`)}</span>
                {z.title}
              </Link>
            )}
            <span className="shrink-0 text-xs text-muted-foreground">{z.onDevice ? t('siteDocs.status.ON_DEVICE') : t(`siteDocs.status.${z.status}`)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
